import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Youtube, Instagram, Facebook, MessageCircle } from "lucide-react";
import { SCHOOL, IMAGES } from "@/lib/site";

const quickLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/academics", label: "Academics" },
  { to: "/gallery", label: "Gallery" },
  { to: "/notices", label: "Notices" },
  { to: "/principal", label: "Principal" },
  { to: "/chairman", label: "Chairman" },
  { to: "/admission", label: "Admission" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-royal-dark text-white pt-16 pb-8 px-6">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-black ring-2 ring-gold/60 overflow-hidden flex items-center justify-center shrink-0">
              <img src={IMAGES.logo} alt="Royal English Medium High School logo" className="w-full h-full object-contain p-1" />
            </div>
            <div>
              <h3 className="font-serif-display text-3xl font-bold leading-tight">Royal English</h3>
              <p className="text-gold text-xs font-semibold tracking-[0.25em]">MEDIUM HIGH SCHOOL</p>
            </div>
          </div>
          <div className="gold-divider" />
          <p className="mt-4 text-gray-300 leading-relaxed max-w-md">
            Empowering students of Chikkamagaluru with quality education, strong values and modern skills since {SCHOOL.established}.
          </p>
          <div className="flex gap-3 mt-6">
            <a data-testid="social-youtube" href={SCHOOL.socials.youtube} target="_blank" rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-all">
              <Youtube size={20} />
            </a>
            <a data-testid="social-instagram" href={SCHOOL.socials.instagram} target="_blank" rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-all">
              <Instagram size={20} />
            </a>
            <a data-testid="social-facebook" href={SCHOOL.socials.facebook} target="_blank" rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-gold hover:text-royal flex items-center justify-center transition-all">
              <Facebook size={20} />
            </a>
            <a data-testid="social-whatsapp" href={`https://wa.me/${SCHOOL.whatsapp}`} target="_blank" rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-green-500 flex items-center justify-center transition-all">
              <MessageCircle size={20} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-lg font-bold mb-5 text-gold">Quick Links</h4>
          <ul className="space-y-2 text-gray-300">
            {quickLinks.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-gold transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-lg font-bold mb-5 text-gold">Contact</h4>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3"><MapPin size={18} className="text-gold mt-1 shrink-0" /><span>{SCHOOL.location}</span></li>
            <li className="flex items-center gap-3"><Phone size={18} className="text-gold shrink-0" />
              <span><a href={`tel:+${SCHOOL.whatsapp}`} className="hover:text-gold">{SCHOOL.whatsappDisplay}</a> / <a href={`tel:+${SCHOOL.phone}`} className="hover:text-gold">{SCHOOL.phoneDisplay}</a></span>
            </li>
            <li className="flex items-center gap-3"><Mail size={18} className="text-gold shrink-0" />
              <a href={`mailto:${SCHOOL.email}`} className="hover:text-gold break-all">{SCHOOL.email}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 mt-12 pt-6 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-gray-400 text-sm">© {new Date().getFullYear()} Royal English Medium High School. All rights reserved.</p>
        <Link to="/admin/login" data-testid="footer-admin-link" className="text-gray-500 hover:text-gold text-xs tracking-widest">ADMIN</Link>
      </div>
    </footer>
  );
}
